import {EtsyListing} from '@coolcolduk/etsy';
import {extractGDriveFolderIdFromUrl, isGDriveSharedLink} from '@coolcolduk/google';
import {useLocalStorageState} from '@coolcolduk/ui-common-hook';
import {useMessage} from '@coolcolduk/ui-mui';
import {extractFilenameWithoutExtension} from '@coolcolduk/util';
import {Button, Stack, TextField} from '@mui/material';
import {get} from 'lodash';
import {useState} from 'react';
import {QueueJobContent} from '../../../__generated__/resolvers-types';
import CheckboxTable from '../../../components/checkboxTable/CheckboxTable';
import EtsyListingTemplateSelector from '../../../components/etsy/EtsyListingTemplateSelector';
import {JobListButton} from '../../../components/jobList/JobListButton';
import TextFieldGoogleDriveLink from '../../../components/textFieldGoogleDriveLink/TextFieldGoogleDriveLink';
import useGetGDriveContent from '../../../hook/useGetGDriveContent';
import useSetEtsyBulkCreateGdriveFile from '../../../hook/useSetEtsyBulkCreateGdriveFile';
import {FeatureProps} from '../../../types/FeatureObject';
import {errorMap} from '../../../util/error/errorMap';
import messageWhenThrown from '../../../util/error/messageWhenThrown';

export enum FilterGDriveImageProduct {
  BOTH = 'both',
  IMAGE = 'image',
  PRODUCT = 'product',
}

export interface EtsyBulkCreateGdriveFileProps extends FeatureProps {}

export default function EtsyBulkCreateGdriveFile({selectedAccount}: EtsyBulkCreateGdriveFileProps) {
  const [sharedLink, setSharedLink] = useState('');
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [imageFolder, setImageFolder] = useLocalStorageState<string>(
    `${QueueJobContent.etsyBulkCreateGdriveFile}-imageFolder`,
    'display',
  );
  const [productFolder, setProductFolder] = useLocalStorageState<string>(
    `${QueueJobContent.etsyBulkCreateGdriveFile}-productFolder`,
    'product',
  );
  const {showMessage} = useMessage();

  const [templateListing, setTemplateListing] = useState<EtsyListing | undefined>();
  const {createBulkEtsyGdriveFileJob} = useSetEtsyBulkCreateGdriveFile();

  const parentFolderId = isGDriveSharedLink(sharedLink) ? extractGDriveFolderIdFromUrl(sharedLink) : undefined;
  const gdriveData = useGetGDriveContent(parentFolderId);

  const productFolderGDriveContent = (gdriveData?.children || []).find((i) => i.name === productFolder);
  const productData = useGetGDriveContent(productFolderGDriveContent?.id);
  const productList = productData?.children || [];
  const contentList = productList.map((d) => extractFilenameWithoutExtension(d.name));
  // show a list of existing job

  return (
    <Stack spacing={2}>
      <JobListButton content={QueueJobContent.etsyBulkCreateGdriveFile} />

      <EtsyListingTemplateSelector
        selectedAccount={selectedAccount}
        selectedListing={templateListing}
        setSelectedListing={setTemplateListing}
      />

      <TextFieldGoogleDriveLink sharedLink={sharedLink} setSharedLink={setSharedLink} />

      <TextField
        fullWidth
        label="Folder containing your listing images"
        value={imageFolder}
        onChange={(e) => setImageFolder(e.target.value)}
      />

      <TextField
        fullWidth
        label="Folder containing your digital product"
        value={productFolder}
        onChange={(e) => setProductFolder(e.target.value)}
        helperText="Change to something not exists if it is not digital product"
      />

      {/* <ButtonGroup variant="contained" aria-label="Filter group" sx={{justifyContent: 'center', mt: 2, mb: 2}}>
        <Button
          onClick={() => setFilterImageProduct(FilterGDriveImageProduct.BOTH)}
          color={filterImageProduct === FilterGDriveImageProduct.BOTH ? 'secondary' : 'primary'}
        >
          Contain both
        </Button>
        <Button
          onClick={() => setFilterImageProduct(FilterGDriveImageProduct.IMAGE)}
          color={filterImageProduct === FilterGDriveImageProduct.IMAGE ? 'secondary' : 'primary'}
        >
          Image only
        </Button>
        <Button
          onClick={() => setFilterImageProduct(FilterGDriveImageProduct.PRODUCT)}
          color={filterImageProduct === FilterGDriveImageProduct.PRODUCT ? 'secondary' : 'primary'}
        >
          Product only
        </Button>
      </ButtonGroup> */}

      <CheckboxTable items={contentList} selectedItems={selectedItems} setSelectedItems={setSelectedItems} />

      <Button
        variant="contained"
        color="primary"
        onClick={async () => {
          if (!templateListing) {
            showMessage('Please select a template', undefined, 'warning');
            return;
          }

          const result = await messageWhenThrown(() =>
            createBulkEtsyGdriveFileJob(
              selectedAccount.id,
              sharedLink,
              JSON.stringify(templateListing),
              imageFolder,
              productFolder,
              productList
                .filter((i) => selectedItems.includes(extractFilenameWithoutExtension(i.name)))
                .map((i) => ({gdriveId: i.id, name: i.name})),
            ),
          );

          showMessage(
            result ? get(errorMap, result, `Job added failed`) : 'Upload job added',
            undefined,
            result ? 'error' : 'success',
          );
          setSelectedItems([]);
        }}
        disabled={!isGDriveSharedLink(sharedLink) || selectedItems.length === 0 || !templateListing}
      >
        Add job
      </Button>
    </Stack>
  );
}
